import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { css } from '@emotion/react';
import { url } from '../../env';
import { AuthContext } from '../../Context/AuthContext';
import { AppButton, AppPasswordField } from '../../Components/FormKit';
import { Cotizador } from './Cotizador';
import {
    clearSourceTrimAccess,
    getSourceTrimAccess,
    isInternalSourceTrimUser,
    SOURCE_TRIM_ACCESS_INVALID_EVENT,
    storeSourceTrimAccess,
} from '../../Utils/sourceTrimAccess';

function SourceTrimAccessGate() {
    const { authInfo } = useContext(AuthContext);
    const [access, setAccess] = useState(() => getSourceTrimAccess());
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);
    const [validationState, setValidationState] = useState(() => access?.token ? 'checking' : 'idle');
    const [error, setError] = useState('');
    const validatingTokenRef = useRef(null);
    const isInternal = isInternalSourceTrimUser(authInfo);

    useEffect(() => {
        const handleInvalidAccess = () => {
            validatingTokenRef.current = null;
            setAccess(null);
            setValidationState('idle');
            setError('Clave invalida');
        };

        window.addEventListener(SOURCE_TRIM_ACCESS_INVALID_EVENT, handleInvalidAccess);
        return () => window.removeEventListener(SOURCE_TRIM_ACCESS_INVALID_EVENT, handleInvalidAccess);
    }, []);

    useEffect(() => {
        if (authInfo?.loading) {
            return;
        }

        if (isInternal) {
            setValidationState('valid');
            return;
        }

        if (!access?.token) {
            setValidationState('idle');
            validatingTokenRef.current = null;
            return;
        }

        if (validatingTokenRef.current === access.token) {
            return;
        }

        const token = access.token;
        validatingTokenRef.current = token;
        setValidationState('checking');

        const body = new URLSearchParams();
        body.append('access_token', token);

        axios.post(
            `${url}/cotizador3/access/validate`,
            body
        ).then(() => {
            if (validatingTokenRef.current === token) {
                setValidationState('valid');
            }
        }).catch(() => {
            if (validatingTokenRef.current === token) {
                clearSourceTrimAccess();
                validatingTokenRef.current = null;
                setAccess(null);
                setValidationState('idle');
            }
        });
    }, [authInfo?.loading, authInfo?.isAuth, authInfo?.user?.type_user, isInternal, access?.token]);

    if (authInfo?.loading || validationState === 'checking') {
        return <div css={sourceTrimGateCSS()}>Cargando...</div>;
    }

    if (isInternal || (access?.token && validationState === 'valid')) {
        return <Cotizador submitEndpointOverride="cotizador3" />;
    }

    const submit = async (event) => {
        event.preventDefault();
        setError('');
        setLoading(true);

        try {
            const res = await axios.post(`${url}/cotizador3/access`, { code: code.trim() });
            if (res.data?.ok && res.data?.access_token && res.data?.expires_at) {
                storeSourceTrimAccess(res.data.access_token, res.data.expires_at);
                validatingTokenRef.current = res.data.access_token;
                setValidationState('valid');
                setAccess({ token: res.data.access_token, expiresAt: res.data.expires_at });
                return;
            }
            setError('Clave invalida');
        } catch {
            setError('Clave invalida');
        } finally {
            setLoading(false);
        }
    };

    return (
        <main css={sourceTrimGateCSS()} className="fk-root">
            <form onSubmit={submit} className="SourceTrimCard">
                <div className="SourceTrimHeader">
                    <span>Cotizador 3</span>
                    <h1>Clave de acceso</h1>
                    <p>Ingresa la clave vigente para abrir la ruta oculta.</p>
                </div>

                <AppPasswordField
                    label="Clave"
                    value={code}
                    onChange={(event) => setCode(event.target.value)}
                    placeholder="Clave"
                    autoComplete="off"
                    autoFocus
                    invalid={!!error}
                    errorMessage={error}
                />
                <AppButton
                    type="submit"
                    label="Entrar"
                    icon="pi pi-lock-open"
                    loading={loading}
                    disabled={!code.trim()}
                    fullWidth
                />
            </form>
        </main>
    );
}

function sourceTrimGateCSS() {
    return css`
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        background: linear-gradient(to bottom, rgba(52, 198, 241, 0.05), #ffffff);
        color: #2e373b;
        padding: 24px;
        font-family: var(--fk-font);

        .SourceTrimCard {
            width: min(100%, 420px);
            display: flex;
            flex-direction: column;
            gap: 16px;
            padding: 28px;
            border-radius: 28px;
            background: rgba(255, 255, 255, 0.86);
            box-shadow: 2px 2px 11px rgba(33, 88, 140, 0.08);
            backdrop-filter: blur(5px);
            animation: sourceTrimCardIn 0.28s ease-out;
        }

        .SourceTrimHeader {
            text-align: center;
            display: flex;
            flex-direction: column;
            gap: 6px;
            margin-bottom: 4px;
        }

        .SourceTrimHeader span {
            color: #22a37e;
            font-size: 0.75rem;
            letter-spacing: 0.14em;
            font-weight: 700;
            text-transform: uppercase;
        }

        h1 {
            margin: 0;
            font-size: 1.35rem;
            font-weight: 700;
            color: #21588C;
            letter-spacing: 0.02em;
        }

        p {
            margin: 0;
            color: #587183;
            font-size: 0.86rem;
            line-height: 1.45;
        }

        @keyframes sourceTrimCardIn {
            from {
                opacity: 0;
                transform: translateY(10px) scale(0.98);
            }
            to {
                opacity: 1;
                transform: translateY(0) scale(1);
            }
        }
    `;
}

export default SourceTrimAccessGate;
